import polyline from '@mapbox/polyline';
import moment from 'moment';
import { BikeSection, PedestrianSection, Place, PublicTransportSection, Route } from '../models';
import { backendCyclingProfiles } from './cycling-profile';
import { HttpService } from './http';
import { UserService } from './user';
export class RouteService {
  static async getRoutes({
    types
  }) {
    if (!UserService.currentUser) throw new Error('user not connected');
    try {
      const data = await HttpService.get('v2', `/users/${UserService.currentUser.id}/routes`, [...types.map(type => ({
        key: 'types[]',
        value: type
      }))]);
      return data.map(parseRoute);
    } catch (err) {
      console.error('[RouteService][getRoutes]', err);
      throw err;
    }
  }
  static async getRoute(id) {
    try {
      const data = await HttpService.get('v2', `/routes/${id}`);
      return parseRoute(data);
    } catch (err) {
      console.error('[RouteService][getRoute]', err);
      throw err;
    }
  }
  static async getGPX(id) {
    try {
      const data = await HttpService.get('v2', `/routes/${id}`, [], [], null, {
        contentType: 'application/gpx+xml',
        responseType: 'blob'
      });
      return data;
    } catch (err) {
      console.error('[RouteService][getGPX]', err);
      throw err;
    }
  }
  static async getRouteFromGPX(gpx, title) {
    try {
      const formData = new FormData();
      formData.append('gpx', gpx);
      formData.append('description', title);
      const response = await HttpService.post('v2', `/route_from_gpx`, [{
        key: 'geometry',
        value: true
      }], [], formData);
      return parseRoute(response);
    } catch (err) {
      console.error('[RouteService][getRouteFromGPX]', err);
      if (typeof err === 'object' && err && 'code' in err) throw err.code;else throw new Error(err.error);
    }
  }
  static async saveRoute({
    type,
    computedRouteId,
    description
  }) {
    try {
      const data = await HttpService.post('v2', `/routes`, [], [], JSON.stringify({
        saved_itinerary_type: type,
        computedRouteId,
        description: description !== undefined ? description : null
      }));
      return parseRoute(data);
    } catch (err) {
      console.error('[RouteService][saveRoute]', err);
      throw err;
    }
  }
  static async updateRoute(id, {
    type,
    computedRouteId,
    description
  }) {
    try {
      const params = {
        saved_itinerary_type: type,
        computedRouteId
      };
      if (description !== undefined) params.description = description;
      const data = await HttpService.put('v2', `/routes/${id}`, [], [], JSON.stringify(params));
      return parseRoute(data);
    } catch (err) {
      console.error('[RouteService][updateRoute]', err);
      throw err;
    }
  }
  static async remove(id) {
    try {
      await HttpService.delete('v2', `/routes/${id}`);
      return true;
    } catch (err) {
      console.error('[RouteService][remove]', err);
      throw err;
    }
  }
}
export const backendBikeTypes = {
  TRADITIONAL: 'own',
  BSS: 'shared'
};
export const bikeTypesMap = {
  own: 'TRADITIONAL',
  shared: 'BSS',
  hybridBike: 'TRADITIONAL',
  cargo: 'TRADITIONAL',
  road: 'TRADITIONAL'
};
export function parseWayPoint({
  latitude,
  longitude,
  title,
  poi_id
}) {
  const parts = (title || '').split(',').map(part => part.trim());
  const [primaryText, secondaryText] = parts.length > 2 ? [parts.slice().splice(0, parts.length - 2).join(', '), parts.slice().splice(-2).join(', ')] : [title, ''];
  const city = parts.length >= 2 ? parts.slice().splice(-2)[0] : undefined;
  return new Place(undefined, {
    type: 'Point',
    coordinates: [longitude, latitude]
  }, title ? {
    primaryText,
    secondaryText,
    city
  } : undefined, poi_id);
}
const facilitiesMap = {
  CYCLEWAY: 'cycleway',
  FORBIDDEN: 'forbidden',
  FERRY: 'ferry',
  FOOTWAY: 'footway',
  GREENWAY: 'greenway',
  LANE: 'lane',
  LIVINGSTREET: 'livingstreet',
  NONE: 'none',
  OPPOSITE: 'opposite',
  PEDESTRIAN: 'pedestrian',
  PRIMARY: 'primary',
  RESIDENTIAL: 'residential',
  SECONDARY: 'secondary',
  SERVICE: 'service',
  SHAREBUSWAY: 'sharebusway',
  STEPS: 'steps',
  TERTIARY: 'tertiary',
  ZONE30: 'zone30'
};
const roadCyclingQualitiesMap = {
  ASPHALT: 'asphalt',
  DAMAGED_TRACKS: 'damagedTracks',
  PATH: 'path',
  UNKNOWN: 'unknown'
};

// Indexe les changements par geometryIndex. L'en-tete et la premiere valeur sont
// ignores : cette derniere sert d'etat initial et ne doit pas declencher de coupe
// a l'index 0.
//
// Sur un index duplique la derniere valeur gagne. Le backend concatene un tronçon
// par etape, chacun se terminant par une valeur sentinelle (UNKNOWN pour les
// revetements) : a la jonction, cette sentinelle partage son index avec la vraie
// valeur qui ouvre le tronçon suivant. C'est bien cette derniere qui decrit la
// suite du trace.
function toChangesMap(changes, valuesMap) {
  const res = new Map();
  changes?.slice(2).forEach(([geometryIndex, value]) => {
    // Le tuple d'en-tete elargit le type des elements apres slice() : on ne garde
    // que les vraies entrees, dont l'index est numerique.
    if (typeof geometryIndex !== 'number') return;
    res.set(geometryIndex, valuesMap[value]);
  });
  return res;
}
function parseInstructions(_instructions) {
  if (!_instructions || _instructions.length < 2) return [];
  const directionsMap = {
    CROSSING: 'crossing',
    DROP_SHARED_BIKE: 'dropSharedBike',
    ELEVATOR: 'elevator',
    ENTER_AGAINST_ALLOWED_DIRECTION: 'enterAgainstAllowedDirection',
    ENTER_ROUND_ABOUT: 'enterRoundAbout',
    ENTER_UNGUIDED_SECTION: 'unknown',
    EXIT_UNGUIDED_SECTION: 'unknown',
    GET_OFF_THE_BIKE: 'getOffTheBike',
    GET_ON_THE_BIKE: 'getOnTheBike',
    GO_STRAIGHT: 'goStraight',
    HEAD_ON: 'headOn',
    STAY_ON: 'stayOn',
    LEAVE_AGAINST_ALLOWED_DIRECTION: 'leaveAgainstAllowedDirection',
    LEAVE_PUBLIC_TRANSPORT: 'leavePublicTransport',
    LEAVE_ROUND_ABOUT: 'leaveRoundAbout',
    REACH_VIA_LOCATION: 'reachViaLocation',
    REACHED_THE_STATION: 'reachedTheStation',
    REACHED_YOUR_DESTINATION: 'reachedYourDestination',
    ROUND_ABOUT_EXIT_1: 'roundAboutExit1',
    ROUND_ABOUT_EXIT_2: 'roundAboutExit2',
    ROUND_ABOUT_EXIT_3: 'roundAboutExit3',
    ROUND_ABOUT_EXIT_4: 'roundAboutExit4',
    ROUND_ABOUT_EXIT_5: 'roundAboutExit5',
    ROUND_ABOUT_EXIT_6: 'roundAboutExit6',
    ROUND_ABOUT_EXIT_7: 'roundAboutExit7',
    ROUND_ABOUT_EXIT_8: 'roundAboutExit8',
    ROUND_ABOUT_EXIT_9: 'roundAboutExit9',
    ROUND_ABOUT_EXIT_10: 'roundAboutExit10',
    ROUND_ABOUT_EXIT_11: 'roundAboutExit11',
    ROUND_ABOUT_EXIT_12: 'roundAboutExit12',
    ROUND_ABOUT_EXIT_13: 'roundAboutExit13',
    ROUND_ABOUT_EXIT_14: 'roundAboutExit14',
    ROUND_ABOUT_EXIT_15: 'roundAboutExit15',
    ROUND_ABOUT_EXIT_16: 'roundAboutExit16',
    ROUND_ABOUT_EXIT_17: 'roundAboutExit17',
    ROUND_ABOUT_EXIT_18: 'roundAboutExit18',
    ROUND_ABOUT_EXIT_19: 'roundAboutExit19',
    ROUND_ABOUT_EXIT_20: 'roundAboutExit20',
    ROUND_ABOUT_EXIT_X: 'roundAboutExitX',
    START_AT_END_OF_STREET: 'startAtTheEndOfStreet',
    STAY_ON_ROUND_ABOUT: 'stayOnRoundAbout',
    TAKE_PUBLIC_TRANSPORT: 'takePublicTransport',
    TAKE_SHARED_BIKE: 'takeSharedBike',
    TURN_LEFT: 'turnLeft',
    TURN_RIGHT: 'turnRight',
    TURN_SHARP_LEFT: 'turnSharpLeft',
    TURN_SHARP_RIGHT: 'turnSharpRight',
    TURN_SLIGHT_LEFT: 'turnSlightLeft',
    TURN_SLIGHT_RIGHT: 'turnSlightRight',
    U_TURN: 'uTurn',
    UNKNOWN: 'unknown'
  };
  const [, ...instructions] = _instructions;
  return instructions.reduce((res, [direction, roadName, roadLength, facility, cyclability, geometryIndex, orientation,, duration]) => {
    res.push({
      direction: directionsMap[direction] || 'unknown',
      roadName,
      distance: roadLength,
      facility: facilitiesMap[facility] || 'none',
      cyclability,
      geometryIndex,
      orientation,
      duration
    });
    return res;
  }, []);
}
function parseElevations(_elevations) {
  if (!_elevations || _elevations.length < 2) return [];
  const [, ...elevations] = _elevations;
  return elevations.reduce((res, [distanceFromStart, elevation, geometryIndex]) => {
    res.push({
      geometryIndex,
      elevation,
      distanceFromStart
    });
    return res;
  }, []);
}
function parseFacilities(_facilities) {
  if (!_facilities || _facilities.length < 2) return [];
  const [, ...facilities] = _facilities;
  return facilities.reduce((res, [geometryIndex, key]) => {
    res.push({
      geometryIndex,
      facility: facilitiesMap[key]
    });
    return res;
  }, []);
}
export function parseSection(props, index, _prevWayPointIndex) {
  const {
    details,
    duration,
    geometry: _geometry,
    waypoints,
    waypointsIndices: _wayPointsIndices,
    estimatedDatetimeOfDeparture: _estimatedDatetimeOfDeparture,
    estimatedDatetimeOfArrival: _estimatedDatetimeOfArrival
  } = props;
  const {
    instructions: _instructions
  } = details;
  const instructions = parseInstructions(_instructions);
  const wayPointsIndices = _wayPointsIndices || [];
  let geometry = {
    type: 'LineString',
    coordinates: []
  };
  const features = [];
  if (_geometry && _wayPointsIndices) {
    const {
      coordinates
    } = geometry = polyline.toGeoJSON(_geometry, 6);
    const stepIndices = _wayPointsIndices.slice(1, -1);
    const getOffBikeIndices = [];
    const getOnBikeIndices = [];
    if ('bikeType' in details) {
      instructions.forEach(({
        direction,
        geometryIndex: index
      }) => {
        if (direction === 'getOffTheBike') getOffBikeIndices.push(index);
        if (direction === 'getOnTheBike') getOnBikeIndices.push(index);
      });
    }
    let prevWayPointIndex = _prevWayPointIndex;
    let startGeometryIndex = 0;
    let walking = false;
    let facility = 'bikeType' in details && details.facilities && details.facilities[1] && facilitiesMap[details.facilities[1][1]] || undefined;
    let roadCyclingQuality = 'bikeType' in details && details.roadCyclingQualities && details.roadCyclingQualities[1] && roadCyclingQualitiesMap[details.roadCyclingQualities[1][1]] || undefined;
    // Indexation prealable : la boucle etait en O(coordonnees x changements) et
    // reallouait un slice() a chaque coordonnee, pour chacune des deux grandeurs.
    const facilityChanges = toChangesMap('bikeType' in details && details.facilities || undefined, facilitiesMap);
    const roadCyclingQualityChanges = toChangesMap('bikeType' in details && details.roadCyclingQualities || undefined, roadCyclingQualitiesMap);
    coordinates.forEach((_, index) => {
      const facilityChanged = facilityChanges.has(index);
      const roadCyclingQualityChanged = roadCyclingQualityChanges.has(index);
      const isStep = stepIndices.indexOf(index) !== -1;
      const getOffBike = getOffBikeIndices.indexOf(index) !== -1;
      const getOnBike = getOnBikeIndices.indexOf(index) !== -1;
      if (isStep || getOffBike || getOnBike || facilityChanged || roadCyclingQualityChanged) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coordinates.slice(startGeometryIndex, index + 1)
          },
          properties: {
            index,
            prevWayPointIndex,
            walking,
            facility,
            roadCyclingQuality
          }
        });
        startGeometryIndex = index;
        if (getOffBike) walking = true;
        if (getOnBike) walking = false;
        if (isStep) prevWayPointIndex += 1;
        if (facilityChanged) facility = facilityChanges.get(index);
        if (roadCyclingQualityChanged) roadCyclingQuality = roadCyclingQualityChanges.get(index);
      }
    });
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coordinates.slice(startGeometryIndex)
      },
      properties: {
        index,
        prevWayPointIndex,
        walking,
        facility,
        roadCyclingQuality
      }
    });
  }
  const wayPoints = waypoints.map(({
    latitude,
    longitude,
    title
  }) => new Place(undefined, {
    type: 'Point',
    coordinates: [longitude, latitude]
  }, title ? {
    primaryText: title
  } : undefined));
  const estimatedDatetimeOfDeparture = moment(_estimatedDatetimeOfDeparture);
  const estimatedDatetimeOfArrival = moment(_estimatedDatetimeOfArrival);
  if (props.transportMode === 'PEDESTRIAN') {
    const {
      direction,
      distances,
      verticalGain,
      verticalLoss,
      calories,
      elevations
    } = props.details;
    return new PedestrianSection(direction, distances.total, duration, verticalGain, verticalLoss, calories || 0, geometry, features, wayPoints, wayPointsIndices, instructions, parseElevations(elevations), estimatedDatetimeOfDeparture, estimatedDatetimeOfArrival);
  }
  if (props.transportMode === 'PUBLIC_TRANSPORT') {
    const {
      distances
    } = props.details;
    return new PublicTransportSection(details, distances.total, duration, geometry, features, wayPoints, wayPointsIndices, estimatedDatetimeOfDeparture, estimatedDatetimeOfArrival);
  }
  const {
    profile,
    electric_adapted_itinerary,
    direction,
    distances,
    verticalGain,
    verticalLoss,
    elevations,
    facilities,
    bikeStations,
    calories
  } = props.details;
  return new BikeSection(backendCyclingProfiles[profile], Boolean(electric_adapted_itinerary), direction, distances.total, duration, verticalGain, verticalLoss, props.calories || calories || 0, geometry, features, wayPoints, wayPointsIndices, instructions, parseElevations(elevations), parseFacilities(facilities), bikeStations ? {
    from: {
      bikeStationId: bikeStations.from.selected
    },
    to: {
      bikeStationId: bikeStations.to.selected
    }
  } : null, estimatedDatetimeOfDeparture, estimatedDatetimeOfArrival);
}
export const routeLabelKeys = {
  FASTER: 'faster',
  RECOMMENDED: 'recommended',
  RIDE: 'ride',
  SAFER: 'safer',
  VTC: 'hybrid_bike',
  CARGO: 'cargo',
  ROAD: 'road'
};
export const routeTypes = {
  RIDE: 'ride',
  USER: 'user',
  USER_GUIDED: 'userGuided',
  USER_TRIP: 'userTrip'
};
export function parseRoute({
  id,
  created,
  description,
  title,
  type,
  waypoints,
  distances,
  duration,
  estimatedDatetimeOfDeparture,
  estimatedDatetimeOfArrival,
  sections,
  computedRouteId
}) {
  let prevWayPointIndex = 0;
  return new Route(id, moment(created), description, routeLabelKeys[title], routeTypes[type], waypoints.map(parseWayPoint), distances, duration, moment(estimatedDatetimeOfDeparture), moment(estimatedDatetimeOfArrival), sections.map((sectionData, index) => {
    const section = parseSection(sectionData, index, prevWayPointIndex);
    prevWayPointIndex += section.wayPointsIndices.length - 2;
    return section;
  }), computedRouteId);
}